import { map, isEmpty, isFunction, isNil } from 'lodash/fp';
import { syntaxTree } from '@codemirror/language';
import { snippet as snippetToFunction } from '@codemirror/autocomplete';
import { stripQuotes } from 'app/lib/utils/stringUtils';
import {
    SnowFormula,
    QuotedIdentifier,
    Identifier,
    IdentifierExt,
    Dot,
    AliasClause,
    SelectBase,
    FromClause,
    JoinSpecification,
    TableObjectName,
    FromExpression,
    BaseFromExpression,
    JoinExpression,
    ExpressionA,
    Star
} from './parser/parser.terms.js';

const WS_RE = /\s/;

function isWhitespace(text, pos = 0) {
    return pos >= text.length || WS_RE.test(text.at(pos));
}

function nearestNonWhitespace(text, startPos) {
    let pos = startPos < text.length ? startPos : text.length - 1;

    while (WS_RE.test(text.at(pos)) && pos > 0) {
        pos--;
    }

    return pos;
}

function getParentNodes(startNode) {
    let node = startNode;
    const nodes = [];

    do {
        nodes.push(node.type.id);

        node = node.parent;
    } while (node);

    return nodes;
}

function findAncestorOfType(startNode, type) {
    let node = startNode;

    do {
        if (node.type.id === type) {
            return node;
        }

        node = node.parent;
    } while (node);

    return null;
}

function inFromClause(nodes) {
    for (const node of nodes) {
        if (node === AliasClause || node === SelectBase || node === JoinSpecification) {
            return false;
        }

        if (node === FromClause) {
            return true;
        }
    }

    return false;
}

function inSelectClause(nodes) {
    for (const node of nodes) {
        if (node === FromClause) {
            return false;
        }

        if (node === SelectBase) {
            return true;
        }
    }

    return false;
}

function inFormula(nodes) {
    for (const node of nodes) {
        if (node === SnowFormula) {
            return true;
        }
    }

    return false;
}

function parseTableNames(node, tree, state) {
    const tables = [];

    const base = findAncestorOfType(node, SelectBase);

    if (isNil(base)) {
        return [];
    }

    const from = base.getChild(FromClause);

    if (isNil(from)) {
        return [];
    }

    const fromExpressions = from.getChildren(FromExpression);

    for (const fromExpression of fromExpressions) {
        const joinExpressions = fromExpression.getChildren(JoinExpression);
        let baseFromExpressions = [];

        for (const joinExpression of joinExpressions) {
            baseFromExpressions = baseFromExpressions.concat(
                joinExpression.getChildren(BaseFromExpression)
            );
        }

        baseFromExpressions = baseFromExpressions.concat(
            fromExpression.getChildren(BaseFromExpression)
        );

        for (const baseFromExpression of baseFromExpressions) {
            const table = baseFromExpression.getChild(TableObjectName);
            const alias = baseFromExpression.nextSibling;

            if (isNil(table)) {
                continue;
            }

            tables.push({
                name: stripQuotes(state.sliceDoc(table.from, table.to)),
                alias:
                    !isNil(alias) && alias.type.id === AliasClause
                        ? state.sliceDoc(alias.from, alias.to)
                        : null
            });
        }
    }

    return tables;
}

function parseIdentifiers(node, state) {
    const cursor = node.cursor();
    const identifiers = [];

    while (
        cursor.type.id === QuotedIdentifier ||
        cursor.type.id === IdentifierExt ||
        cursor.type.id === Identifier ||
        cursor.type.id === Dot
    ) {
        if (cursor.type.id === Identifier || cursor.type.id === QuotedIdentifier) {
            identifiers.push({
                type: cursor.type.id,
                value: stripQuotes(state.sliceDoc(cursor.from, cursor.to))
            });
        }

        cursor.prev();
    }

    if (node.name === 'Dot') {
        identifiers.unshift({
            type: Dot,
            value: ''
        });
    }

    return identifiers;
}

export function completeTableEntities(callback) {
    return (context) => {
        if (!isFunction(callback)) {
            return null;
        }

        const tree = syntaxTree(context.state);
        const text = !isNil(context.state.doc.text) ? context.state.doc.text[0] : '';

        const nodeBefore = tree.resolveInner(context.pos, -1);
        const identifiers = parseIdentifiers(nodeBefore, context.state);

        if (identifiers.length > 0) {
            const parents = getParentNodes(nodeBefore);

            if (inFromClause(parents)) {
                const options = callback(map('value', identifiers));
                let from = nodeBefore.from;
                let to = nodeBefore.to;

                if (identifiers[0].type === Dot) {
                    from = context.pos;
                    to = context.pos;
                } else if (identifiers[0].type === QuotedIdentifier) {
                    from = nodeBefore.from + 1;
                }

                return { from, to, options };
            }
        } else if (context.explicit && isWhitespace(text, context.pos)) {
            const previousTokenEndPos = nearestNonWhitespace(text, context.pos);
            const previousToken = tree.resolveInner(previousTokenEndPos, -1);

            const parents = getParentNodes(previousToken);

            if (inFromClause(parents)) {
                return {
                    from: context.pos,
                    to: context.pos,
                    options: callback([''])
                };
            }
        }

        return null;
    };
}

export function completeKeywords(keywords) {
    return (context) => {
        const tree = syntaxTree(context.state);
        const nodeBefore = tree.resolveInner(context.pos, -1);

        const options = keywords.map((keyword) => ({
            label: keyword.toUpperCase(),
            detail: ' ',
            apply: `${keyword.toUpperCase()} `,
            type: 'keyword',
            color: 'keyword'
        }));

        return {
            from: context.explicit ? context.pos : nodeBefore.from,
            to: context.explicit ? context.pos : nodeBefore.to,
            options
        };
    };
}

export function completeSnippets(snippets) {
    return (context) => {
        const word = context.matchBefore(/\w*/);
        const tree = syntaxTree(context.state);
        const nodeBefore = tree.resolveInner(context.pos, -1);
        const parents = getParentNodes(nodeBefore);

        if (!inSelectClause(parents) && !inFormula(parents)) {
            return null;
        }

        const text = context.state.sliceDoc(0);
        const nextCharIsWhitespace = isWhitespace(text, context.pos);
        const nextCharIsComma = text.at(context.pos) === ',';
        let firstParam;

        if (!nextCharIsWhitespace && !nextCharIsComma) {
            const nodeAfter = tree.resolveInner(context.pos, 1);
            const nextExpression =
                nodeAfter.type.id === Star || nodeAfter.type.id === QuotedIdentifier
                    ? nodeAfter
                    : findAncestorOfType(nodeAfter, ExpressionA);

            if (nextExpression) {
                firstParam = context.state.sliceDoc(word.to, nextExpression.to).trim();
            }
        }

        const options = snippets.map((snippet) => ({
            label: snippet.name,
            detail: snippet.summary,
            type: 'function',
            color: 'function',
            apply: createFunctionCompletion(snippet, firstParam),
            boost: 80
        }));

        return {
            from: context.explicit ? word.from : nodeBefore.from,
            to: word.to,
            options
        };
    };
}

function createFunctionCompletion(snippet, firstParam) {
    if (firstParam) {
        const { completion, numberOfArguments } = inlineFirstParameter(
            snippet.completion,
            firstParam
        );
        if (numberOfArguments > 0) {
            return (view, _, from, to) => {
                snippetToFunction(completion)(view, _, from, to + firstParam.length);
                if (numberOfArguments === 1) {
                    view.dispatch({
                        selection: { anchor: from + completion.length }
                    });
                }
            };
        }
    }
    return snippetToFunction(snippet.completion);
}

function inlineFirstParameter(completion, firstParam) {
    let matches = 0;
    const templateWithFirstParameterInserted = completion.replace(/\$\{.+?\}/g, (match) => {
        const replacement = matches === 0 ? firstParam : match;
        matches++;
        return replacement;
    });
    return {
        numberOfArguments: matches,
        completion: templateWithFirstParameterInserted
    };
}

export function completeColumns(callback) {
    return (context) => {
        if (!isFunction(callback)) {
            return null;
        }

        const tree = syntaxTree(context.state);
        const text = !isNil(context.state.doc.text) ? context.state.doc.text[0] : '';

        const nodeBefore = tree.resolveInner(context.pos, -1);
        const identifiers = parseIdentifiers(nodeBefore, context.state);
        const tables = parseTableNames(nodeBefore, tree, context.state);

        if (isEmpty(tables)) {
            return null;
        }

        if (identifiers.length > 0) {
            const parents = getParentNodes(nodeBefore);

            if (!inFromClause(parents)) {
                const options = callback(map('value', identifiers), tables);
                let from = nodeBefore.from;
                let to = nodeBefore.to;

                if (identifiers[0].type === Dot) {
                    from = context.pos;
                    to = context.pos;
                } else if (identifiers[0].type === QuotedIdentifier) {
                    from = nodeBefore.from + 1;
                }

                return { from, to, options };
            }
        } else if (context.explicit && isWhitespace(text, context.pos)) {
            const previousTokenEndPos = nearestNonWhitespace(text, context.pos);
            const previousToken = tree.resolveInner(previousTokenEndPos, -1);

            const parents = getParentNodes(previousToken);

            if (!inFromClause(parents)) {
                const options = callback([''], tables);

                return {
                    from: context.pos,
                    to: context.pos,
                    options
                };
            }
        }

        return null;
    };
}

export function completeFormulaColumns(callback) {
    return (context) => {
        if (!isFunction(callback)) {
            return null;
        }

        const options = callback();

        if (!options) {
            return null;
        }

        const tree = syntaxTree(context.state);
        const nodeBefore = tree.resolveInner(context.pos, -1);
        const identifiers = parseIdentifiers(nodeBefore, context.state);

        let from = context.explicit ? context.pos : nodeBefore.from;
        const to = context.explicit ? context.pos : nodeBefore.to;

        if (identifiers[0]?.type === QuotedIdentifier) {
            from = nodeBefore.from + 1;
        }

        return {
            from,
            to,
            options
        };
    };
}
